import Vue from 'vue';
import VueRouter from 'vue-router';
import Homepage from 'Views/Homepage';
import Login from 'Views/Login';
import Register from 'Views/Register';
import Dashboard from 'Views/Dashboard';
import PasswordReset from 'Views/PasswordReset';
import OfferUpload from 'Components/offer/OfferUpload';
import SellingOffer from 'Views/SellingOffer';
import DemandOverview from 'Views/DemandOverview';
import Admin from 'Views/admin/Admin';
import AdminSellingOffer from 'Views/admin/AdminSellingOffer';
import AdminDemand from 'Views/admin/AdminDemand';
import AdminDemandOverview from 'Views/admin/AdminDemandOverview';
import AdminPurchaseOrderOverview from 'Views/admin/AdminPurchaseOrderOverview';
import AdminAcceptedPurchaseOffers from '@/views/admin/AdminAcceptedPurchaseOffers';
import BulkUploadOverview from 'Views/BulkUploadOverview';

import store from '@/store';
import {
  hasUserAnyOfThesePermissions,
  hasUserAllOfThesePermissions,
  isUserLoggedIn,
  getRefreshToken,
  getAccessToken,
  getUser,
} from '@/auth';
import jwt from 'jsonwebtoken';
import Users from '@/views/UsersView';

Vue.use(VueRouter);

const router = new VueRouter({
  routes: [
    {
      path: '/',
      name: 'homepage',
      component: Homepage,
      beforeEnter(to, from, next) {
        // If the user is already logged in, redirect to the dashboard.
        if (isUserLoggedIn()) {
          next({ name: 'dashboard' });
        } else {
          next();
        }
      },
    },
    {
      path: '/dashboard',
      name: 'dashboard',
      component: Dashboard,
      meta: {
        auth: {
          requiresLogin: true,
        },
      },
    },
    {
      path: '/login',
      name: 'login',
      component: Login,
      beforeEnter(to, from, next) {
        if (getUser() !== false) {
          next({ name: 'dashboard' });
        } else {
          next();
        }
      },
    },
    {
      path: '/register',
      name: 'register',
      component: Register,
      meta: {
        auth: {
          permissions: ['user_service.users.create'],
          permissionType: 'any',
        },
      },
    },
    {
      path: '/password-reset',
      name: 'password-reset',
      component: PasswordReset,
      meta: {
        auth: {
          permissions: ['user_service.users.update'],
          permissionType: 'any',
        },
      },
    },
    {
      path: '/offers/upload',
      name: 'offers-upload',
      component: OfferUpload,
      meta: {
        auth: {
          permissions: ['offer_service.offers.upload'],
          permissionType: 'any',
        },
      },
    },
    {
      path: '/admin',
      name: 'admin',
      component: Admin,
      meta: {
        auth: {
          permissions: ['offer_service.offers.update'],
          permissionType: 'any',
        },
      },
    },
    {
      path: '/admin/sellingoffers',
      name: 'admin-sellingoffers',
      component: AdminSellingOffer,
      meta: {
        auth: {
          permissions: ['offer_service.offers.update'],
          permissionType: 'any',
        },
      },
    },
    {
      path: '/sellingoffers',
      name: 'selling-offers',
      component: SellingOffer,
      meta: {
        auth: {
          requiresLogin: true,
          permissions: ['offer_service.bids.create'],
        },
      },
    },
    {
      path: '/admin/purchaseoffers',
      name: 'admin-demands',
      component: AdminDemand,
      meta: {
        auth: {
          permissions: ['demand_service.demands.read'],
          permissionType: 'any',
        },
      },
    },
    {
      path: '/admin/purchaseorders',
      name: 'admin-orders',
      component: AdminPurchaseOrderOverview,
      meta: {
        auth: {
          permissions: ['purchase_order.order.read'],
          permissionType: 'any',
        },
      },
    },
    {
      path: '/admin/demands',
      name: 'admin-demand-overview',
      component: AdminDemandOverview,
      meta: {
        auth: {
          permissions: ['demand_service.demands.read'],
          permissionType: 'any',
        },
      },
    },
    {
      path: '/demand',
      name: 'buying-demand',
      component: DemandOverview,
      meta: {
        auth: {
          requiresLogin: true,
          permissions: ['offer_service.purchase_offers.create'],
        },
      },
    },
    {
      path: '/stocklist-upload',
      name: 'bulk-upload',
      component: BulkUploadOverview,
      meta: {
        auth: {
          requiresLogin: true,
          permissions: [
            'offer_service.bulk.read',
            'offer_service.bulk.upload'
          ],
        },
      },
    },
    {
      path: '/admin/accepted-purchaseoffers',
      name: 'admin-accepted-purchaseoffers',
      component: AdminAcceptedPurchaseOffers,
      meta: {
        auth: {
          permissions: ['offer_service.purchase_offers.read_accepted'],
          permissionType: 'any',
        },
      },
    },
    {
      path: '/users',
      name: 'users',
      component: Users,
      meta: {
        auth: {
          permissions: ['user_service.users.update'],
          permissionType: 'any',
        },
      },
    },
    {
      path: '/healthcheck',
      name: 'healthcheck',
      component: Homepage,
    },
  ],
  mode: 'history',
});

/**
 * Router AuthGuard
 *
 * Checks if the route, the user wants to visit, needs some special permissions and if the user has them.
 *
 * To secure a route, put in a meta "auth" object like so:
 *
 *
 * meta: {
 *   auth: {
 *     permissions: [
 *       'supply.watches.create'
 *     ],
 *     permissionType: 'any'
 *   }
 * }
 *
 *
 * or like so:
 *
 * meta: {
 *   auth: {
 *     requiresLogin: true
 *   }
 * }
 *
 *
 * Explanation:
 *
 * permissions : []
 *
 * An array of permissions, the user must have to visit this site. Depending on the setting of the permissionType,
 * the user either needs to have all or any of these permissions.
 *
 *
 * permissionType: 'any' | 'all'
 *
 * If set to 'all', the user must have every permission, given within the permissions array.
 * If set to 'any', the user only needs 1 of the permissions.
 * If anything else or nothing is set, this defaults to 'any'
 *
 *
 * requiresLogin: true
 *
 * The user needs to be logged in (This is automatically assumed, if you ask for permissions,
 * thus it's redundant to add permissions AND the requiresLogin flag.)
 *
 *
 */
router.beforeEach((to, from, next) => {
  if (getAccessToken() !== null && jwt.decode(getRefreshToken()).exp > Date.now() / 1000) {
    console.log('[router] yes, user is logged in');
  } else {
    console.log('[router] no, user is NOT logged in');
    store.dispatch('logout').then(() => {
      next({ name: 'login' });
    });
  }

  // These aren't the droids we're looking for...
  if (!to.meta.auth) {
    //... move along!
    next();
    return;
  }

  let auth = to.meta.auth;

  // Refresh the user to make sure he's logged in, if a valid token exists in the local storage.
  // This helps in cases like when the user has entered a url manually or is refreshing the site.
  // In both cases, we still can log in the user and check his permissions.
  store.dispatch('refreshUser').then(() => {
    // Does this route require permission(s)?
    if (auth.permissions && auth.permissions.length > 0) {
      if (auth.permissionType === 'all' && hasUserAllOfThesePermissions(auth.permissions)) {
        next();
        return;
      }

      if (hasUserAnyOfThesePermissions(auth.permissions)) {
        next();
        return;
      }

      if (isUserLoggedIn()) {
        alert('No permission, to visit ' + to.path);
        // If from.name is not set, the user may have refreshed the site or entered the url manually, thus we redirect
        // to the homepage. Else we just don't navigate.
        from.name ? next(false) : next({ name: 'homepage' });
      } else {
        // case: user requires permission, but is not logged in, so
        // we redirect to login page, and save the path in the URL
        next({
          name: 'login',
          query: Object.assign({}, to.query, {
            p: to.path,
          }),
        });
      }

      // User needs no permissions, only to be logged in.
    } else if (auth.requiresLogin) {
      if (isUserLoggedIn()) {
        next();
      } else {
        // alert('You need to be logged in, to visit ' + to.path)
        next({
          name: 'login',
          query: Object.assign({}, to.query, {
            p: to.path,
          }),
        });
      }
    }
  });
});

export default router;
